import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { tableLabel } from "@/lib/tables";
import AppHeader from "@/components/AppHeader";
import { Plus, Pencil, Trash2, X, Users, History, Search, LayoutGrid, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "", price: "", available: true };

export default function AdminDashboard() {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [waiters, setWaiters] = useState([]);
    const [newWaiter, setNewWaiter] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/menu");
            setItems(data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);

    const loadWaiters = useCallback(async () => {
        try {
            const { data } = await api.get("/users/garsonlar");
            setWaiters(data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);

    const addWaiter = async () => {
        const username = newWaiter.trim().toLowerCase();
        if (!username) return;
        try {
            const { data } = await api.post("/users/garsonlar", { username });
            toast.success(`${data.username} eklendi · şifre: ${data.password_hint}`);
            setNewWaiter("");
            loadWaiters();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    const removeWaiter = async (id, username) => {
        if (!window.confirm(`${username} silinsin mi?`)) return;
        try {
            await api.delete(`/users/garsonlar/${id}`);
            toast.success("Garson silindi");
            loadWaiters();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    const [allUsers, setAllUsers] = useState([]);
    const [pwEditing, setPwEditing] = useState(null); // user id
    const [pwValue, setPwValue] = useState("");

    const loadAllUsers = useCallback(async () => {
        try {
            const { data } = await api.get("/users");
            setAllUsers(data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);

    const savePassword = async (userId) => {
        if (pwValue.length < 4) {
            toast.error("Şifre en az 4 karakter olmalı");
            return;
        }
        try {
            const { data } = await api.patch(`/users/${userId}/password`, { new_password: pwValue });
            toast.success(`${data.username} şifresi değiştirildi`);
            setPwEditing(null);
            setPwValue("");
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    useEffect(() => {
        loadAllUsers();
    }, [loadAllUsers]);

    const [history, setHistory] = useState([]);
    const [historyDate, setHistoryDate] = useState("");
    const [historyDetail, setHistoryDetail] = useState(null);

    const loadHistory = useCallback(async (dateStr = "") => {
        try {
            const q = dateStr ? `?date=${dateStr}` : "";
            const { data } = await api.get(`/orders/history${q}`);
            setHistory(data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const fmtDate = (iso) => {
        if (!iso) return "-";
        const d = new Date(iso);
        return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };

    // --- Table layout config ---
    const [layoutGroups, setLayoutGroups] = useState([]);
    const loadLayout = useCallback(async () => {
        try {
            const { data } = await api.get("/config/tables");
            setLayoutGroups(data.groups || []);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);
    useEffect(() => { loadLayout(); }, [loadLayout]);

    const updateLayout = (idx, field, val) => {
        setLayoutGroups((g) => g.map((row, i) => i === idx ? { ...row, [field]: field === "count" ? Math.max(1, parseInt(val, 10) || 1) : val } : row));
    };
    const addLayoutGroup = () => setLayoutGroups((g) => [...g, { label: "Yeni Bölüm", count: 1 }]);
    const removeLayoutGroup = (idx) => setLayoutGroups((g) => g.filter((_, i) => i !== idx));
    const saveLayout = async () => {
        try {
            const payload = { groups: layoutGroups.map((g) => ({ label: g.label.trim(), count: Number(g.count) })) };
            if (payload.groups.some((g) => !g.label)) { toast.error("Bölüm adı boş olamaz"); return; }
            const { data } = await api.put("/config/tables", payload);
            toast.success(`Kaydedildi (${data.total} masa). Sayfa yenileniyor...`);
            const { setTableGroups } = await import("@/lib/tables");
            setTableGroups(data.groups);
            setTimeout(() => window.location.reload(), 900);
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    // --- Sales analytics ---
    const [salesDate, setSalesDate] = useState("");
    const [sales, setSales] = useState({ items: [], total_orders: 0, total_revenue: 0 });
    const loadSales = useCallback(async (d = "") => {
        try {
            const q = d ? `?date=${d}` : "";
            const { data } = await api.get(`/analytics/sales${q}`);
            setSales(data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);
    useEffect(() => { loadSales(); }, [loadSales]);

    useEffect(() => {
        load();
        loadWaiters();
    }, [load, loadWaiters]);

    const openCreate = () => {
        setForm(EMPTY);
        setEditingId(null);
        setShowForm(true);
    };

    const openEdit = (item) => {
        setForm({
            name: item.name,
            category: item.category,
            price: String(item.price),
            available: item.available,
        });
        setEditingId(item.id);
        setShowForm(true);
    };

    const close = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(EMPTY);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.category.trim() || !form.price) {
            toast.error("Tüm alanları doldurun");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                category: form.category.trim(),
                price: parseFloat(form.price),
                available: form.available,
            };
            if (editingId) {
                await api.put(`/menu/${editingId}`, payload);
                toast.success("Yemek güncellendi");
            } else {
                await api.post("/menu", payload);
                toast.success("Yemek eklendi");
            }
            close();
            load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Bu yemeği silmek istediğinize emin misiniz?")) return;
        try {
            await api.delete(`/menu/${id}`);
            toast.success("Yemek silindi");
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    const toggleAvail = async (item) => {
        try {
            await api.put(`/menu/${item.id}`, {
                name: item.name,
                category: item.category,
                price: item.price,
                available: !item.available,
            });
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    const grouped = items.reduce((acc, item) => {
        (acc[item.category] = acc[item.category] || []).push(item);
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-[#FAF9F6]">
            <AppHeader title="Yönetici Paneli" />
            <main className="max-w-[1400px] mx-auto px-6 md:px-8 py-8">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">Menü Yönetimi</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#2C302E]">Yemekler</h1>
                        <p className="text-sm text-[#59605B] mt-2">{items.length} yemek listede</p>
                    </div>
                    <button
                        onClick={openCreate}
                        data-testid="add-menu-item"
                        className="inline-flex items-center gap-2 bg-[#D96C4A] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#C05A3B] active:scale-[0.99] transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        Yeni Yemek
                    </button>
                </div>

                <div className="space-y-8">
                    <section>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] flex items-center gap-2 mb-3">
                            <Users className="w-3.5 h-3.5" /> Tüm Kullanıcılar & Şifreler
                        </div>
                        <div className="bg-white border border-[#E5E3D8] rounded-2xl divide-y divide-[#E5E3D8]">
                            {allUsers.map((u) => (
                                <div key={u.id} data-testid={`user-row-${u.username}`} className="flex items-center justify-between gap-3 px-5 py-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-[#2C302E]">{u.username}</div>
                                        <div className="text-xs text-[#59605B] uppercase tracking-wider">{u.role}{u.name ? ` · ${u.name}` : ""}</div>
                                    </div>
                                    {pwEditing === u.id ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                autoFocus
                                                value={pwValue}
                                                onChange={(e) => setPwValue(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && savePassword(u.id)}
                                                placeholder="yeni şifre (min 4)"
                                                data-testid={`pw-input-${u.username}`}
                                                className="bg-white border border-[#D96C4A] rounded-lg px-3 py-2 text-sm w-40 focus:outline-none focus:ring-1 focus:ring-[#D96C4A]"
                                            />
                                            <button onClick={() => savePassword(u.id)} data-testid={`pw-save-${u.username}`} className="px-3 py-2 rounded-lg bg-[#D96C4A] text-white text-xs font-semibold">Kaydet</button>
                                            <button onClick={() => { setPwEditing(null); setPwValue(""); }} className="px-3 py-2 rounded-lg bg-[#F3F1EB] text-[#2C302E] text-xs font-medium">İptal</button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => { setPwEditing(u.id); setPwValue(""); }}
                                            data-testid={`change-pw-${u.username}`}
                                            className="text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-lg bg-[#F3F1EB] text-[#2C302E] hover:bg-[#E5E3D8]"
                                        >
                                            Şifre Değiştir
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] flex items-center gap-2 mb-3">
                            <LayoutGrid className="w-3.5 h-3.5" /> Masa Düzeni
                        </div>
                        <div className="bg-white border border-[#E5E3D8] rounded-2xl p-5">
                            <div className="text-xs text-[#59605B] mb-4">
                                Bölümlerin adını ve masa sayısını değiştirebilirsin. Sıralama masaları numaralandırır (1&apos;den başlayarak). Kaydettikten sonra sayfa otomatik yenilenir.
                            </div>
                            <div className="space-y-2 mb-4">
                                {layoutGroups.map((g, idx) => {
                                    const start = layoutGroups.slice(0, idx).reduce((s, x) => s + Number(x.count || 0), 0) + 1;
                                    const end = start + Number(g.count || 0) - 1;
                                    return (
                                        <div key={idx} data-testid={`layout-row-${idx}`} className="flex flex-wrap items-center gap-2">
                                            <input
                                                value={g.label}
                                                onChange={(e) => updateLayout(idx, "label", e.target.value)}
                                                placeholder="Bölüm adı (Cam, Orta, Dış Sağ...)"
                                                className="flex-1 min-w-[140px] bg-white border border-[#E5E3D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#D96C4A]"
                                            />
                                            <input
                                                type="number"
                                                min="1"
                                                max="50"
                                                value={g.count}
                                                onChange={(e) => updateLayout(idx, "count", e.target.value)}
                                                className="w-20 bg-white border border-[#E5E3D8] rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:border-[#D96C4A]"
                                            />
                                            <div className="text-[11px] text-[#59605B] w-24">Masa {start}-{end}</div>
                                            <button
                                                onClick={() => removeLayoutGroup(idx)}
                                                data-testid={`layout-del-${idx}`}
                                                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#59605B] hover:bg-red-50 hover:text-red-600"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={addLayoutGroup} data-testid="layout-add" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F3F1EB] text-[#2C302E] text-sm font-medium">
                                    <Plus className="w-4 h-4" /> Bölüm Ekle
                                </button>
                                <button onClick={saveLayout} data-testid="layout-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#D96C4A] text-white text-sm font-semibold hover:bg-[#C05A3B] ml-auto">
                                    Kaydet
                                </button>
                            </div>
                        </div>
                    </section>

                    <section>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] flex items-center gap-2 mb-3">
                            <TrendingUp className="w-3.5 h-3.5" /> Satış Analizi
                        </div>
                        <div className="bg-white border border-[#E5E3D8] rounded-2xl p-5">
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                <input
                                    type="date"
                                    value={salesDate}
                                    onChange={(e) => { setSalesDate(e.target.value); loadSales(e.target.value); }}
                                    data-testid="sales-date"
                                    className="bg-white border border-[#E5E3D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#D96C4A]"
                                />
                                {salesDate && (
                                    <button onClick={() => { setSalesDate(""); loadSales(""); }} className="text-xs px-3 py-2 rounded-lg bg-[#F3F1EB] text-[#2C302E]">Temizle</button>
                                )}
                                <div className="ml-auto flex gap-4 text-xs text-[#59605B]">
                                    <span><b className="text-[#2C302E]">{sales.total_orders}</b> sipariş</span>
                                    <span><b className="text-[#D96C4A]">₺{Number(sales.total_revenue || 0).toFixed(2)}</b> ciro</span>
                                </div>
                            </div>
                            {sales.items.length === 0 ? (
                                <div className="py-6 text-center text-sm text-[#59605B]">Kayıt yok.</div>
                            ) : (
                                <div className="divide-y divide-[#E5E3D8] max-h-[420px] overflow-y-auto">
                                    <div className="flex items-center gap-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#59605B]">
                                        <div className="flex-1">Ürün</div>
                                        <div className="w-16 text-right">Adet</div>
                                        <div className="w-24 text-right">Ciro</div>
                                    </div>
                                    {sales.items.map((r) => (
                                        <div key={r.name} data-testid={`sales-row-${r.name}`} className="flex items-center gap-3 py-2 text-sm">
                                            <div className="flex-1 text-[#2C302E]">{r.name}</div>
                                            <div className="w-16 text-right font-semibold tabular-nums">{r.qty}</div>
                                            <div className="w-24 text-right text-[#D96C4A] font-semibold tabular-nums">₺{r.revenue.toFixed(2)}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>


                    <section>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] flex items-center gap-2 mb-3">
                            <History className="w-3.5 h-3.5" /> Sipariş Geçmişi
                        </div>
                        <div className="bg-white border border-[#E5E3D8] rounded-2xl p-5">
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={historyDate}
                                        onChange={(e) => { setHistoryDate(e.target.value); loadHistory(e.target.value); }}
                                        data-testid="history-date"
                                        className="bg-white border border-[#E5E3D8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#D96C4A]"
                                    />
                                </div>
                                {historyDate && (
                                    <button
                                        onClick={() => { setHistoryDate(""); loadHistory(""); }}
                                        className="text-xs px-3 py-2 rounded-lg bg-[#F3F1EB] text-[#2C302E]"
                                    >
                                        Temizle
                                    </button>
                                )}
                                <div className="ml-auto text-xs text-[#59605B] flex items-center gap-1">
                                    <Search className="w-3 h-3" /> {history.length} kayıt
                                </div>
                            </div>
                            {history.length === 0 ? (
                                <div className="py-6 text-center text-sm text-[#59605B]">Kayıt bulunamadı.</div>
                            ) : (
                                <div className="divide-y divide-[#E5E3D8] max-h-[420px] overflow-y-auto">
                                    {history.map((o) => {
                                        const total = o.items.reduce((s, i) => s + i.price * i.qty, 0);
                                        return (
                                            <button
                                                key={o.id}
                                                onClick={() => setHistoryDetail(o)}
                                                data-testid={`history-row-${o.id}`}
                                                className="w-full flex items-center justify-between gap-3 py-3 hover:bg-[#FAF9F6] px-2 rounded-md text-left"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold text-[#2C302E]">{tableLabel(o.table_number)}</div>
                                                    <div className="text-[11px] text-[#59605B]">
                                                        Alındı: {fmtDate(o.created_at)} · Kapandı: {fmtDate(o.completed_at)}
                                                    </div>
                                                    <div className="text-[11px] text-[#59605B]">Garson: {o.waiter_username}</div>
                                                </div>
                                                <div className="font-display text-lg font-semibold text-[#D96C4A] tabular-nums">
                                                    ₺{total.toFixed(2)}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </section>

                    {historyDetail && (
                        <div className="fixed inset-0 z-50 bg-[#2C302E]/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={() => setHistoryDetail(null)}>
                            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl fade-up" onClick={(e) => e.stopPropagation()}>
                                <div className="bg-[#2C302E] text-white px-6 py-4 flex items-start justify-between">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.2em] opacity-70 mb-1">Geçmiş Sipariş</div>
                                        <div className="font-display text-2xl font-semibold">{tableLabel(historyDetail.table_number)}</div>
                                        <div className="text-xs opacity-70 mt-1">Alındı: {fmtDate(historyDetail.created_at)}</div>
                                        <div className="text-xs opacity-70">Kapandı: {fmtDate(historyDetail.completed_at)}</div>
                                        <div className="text-xs opacity-70">Garson: {historyDetail.waiter_username}</div>
                                    </div>
                                    <button onClick={() => setHistoryDetail(null)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10">✕</button>
                                </div>
                                <div className="px-6 py-4 overflow-y-auto space-y-3">
                                    {historyDetail.items.map((it) => (
                                        <div key={it.id} className="flex justify-between items-start pb-2 border-b border-dashed border-[#E5E3D8] last:border-0">
                                            <div className="flex-1">
                                                <div className="text-sm text-[#2C302E]"><span className="font-semibold">{it.qty}×</span> {it.name}</div>
                                                {it.notes && <div className="text-xs text-[#59605B]">Not: {it.notes}</div>}
                                                {it.added_by === "mutfak" && <div className="text-[10px] font-bold uppercase text-[#D96C4A]">Mutfaktan eklendi</div>}
                                            </div>
                                            <div className="text-sm font-medium text-[#2C302E] tabular-nums">₺{(it.price * it.qty).toFixed(2)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="px-6 py-4 border-t border-[#E5E3D8] bg-[#FAF9F6] flex items-baseline justify-between">
                                    <span className="text-sm text-[#59605B]">Toplam</span>
                                    <span className="font-display text-2xl font-semibold text-[#2C302E]">₺{historyDetail.items.reduce((s, i) => s + i.price * i.qty, 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}


                    <section>
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] flex items-center gap-2">
                                <Users className="w-3.5 h-3.5" /> Garsonlar
                            </div>
                            <div className="text-xs text-[#59605B]">Şifre otomatik: <code className="text-[#2C302E] font-mono">kullanıcı_adı + 123</code></div>
                        </div>
                        <div className="bg-white border border-[#E5E3D8] rounded-2xl p-5">
                            <div className="flex gap-2 mb-4">
                                <input
                                    value={newWaiter}
                                    onChange={(e) => setNewWaiter(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addWaiter()}
                                    placeholder="ahmet"
                                    data-testid="new-waiter-input"
                                    className="flex-1 bg-white border border-[#E5E3D8] rounded-lg px-4 py-2.5 text-[#2C302E] focus:outline-none focus:border-[#D96C4A] focus:ring-1 focus:ring-[#D96C4A]"
                                />
                                <button
                                    onClick={addWaiter}
                                    data-testid="add-waiter"
                                    className="inline-flex items-center gap-2 bg-[#D96C4A] text-white font-medium px-5 py-2.5 rounded-lg hover:bg-[#C05A3B]"
                                >
                                    <Plus className="w-4 h-4" /> Garson Ekle
                                </button>
                            </div>
                            {waiters.length === 0 ? (
                                <div className="text-sm text-[#59605B] py-3">Kayıtlı garson yok.</div>
                            ) : (
                                <div className="divide-y divide-[#E5E3D8]">
                                    {waiters.map((w) => (
                                        <div key={w.id} data-testid={`waiter-${w.username}`} className="flex items-center justify-between py-3">
                                            <div>
                                                <div className="font-medium text-[#2C302E]">{w.username}</div>
                                                <div className="text-xs text-[#59605B]">Şifre: {w.username}123</div>
                                            </div>
                                            <button
                                                onClick={() => removeWaiter(w.id, w.username)}
                                                data-testid={`delete-waiter-${w.username}`}
                                                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#59605B] hover:bg-red-50 hover:text-red-600"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {Object.entries(grouped).map(([category, list]) => (
                        <section key={category}>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-3">
                                {category}
                            </div>
                            <div className="bg-white border border-[#E5E3D8] rounded-2xl divide-y divide-[#E5E3D8] overflow-hidden">
                                {list.map((item) => (
                                    <div
                                        key={item.id}
                                        data-testid={`menu-row-${item.id}`}
                                        className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#FAF9F6] transition-colors"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-[#2C302E]">{item.name}</div>
                                            <div className="text-sm text-[#59605B]">₺{item.price.toFixed(2)}</div>
                                        </div>
                                        <button
                                            onClick={() => toggleAvail(item)}
                                            data-testid={`toggle-avail-${item.id}`}
                                            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                                                item.available
                                                    ? "bg-[#6B8E7B]/15 text-[#6B8E7B] hover:bg-[#6B8E7B]/25"
                                                    : "bg-[#F3F1EB] text-[#59605B] hover:bg-[#E5E3D8]"
                                            }`}
                                        >
                                            {item.available ? "Mevcut" : "Tükendi"}
                                        </button>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => openEdit(item)}
                                                data-testid={`edit-${item.id}`}
                                                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#59605B] hover:bg-[#F3F1EB] transition-colors"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => remove(item.id)}
                                                data-testid={`delete-${item.id}`}
                                                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#59605B] hover:bg-red-50 hover:text-red-600 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </main>

            {showForm && (
                <div className="fixed inset-0 z-50 bg-[#2C302E]/40 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full shadow-xl border border-[#E5E3D8] fade-up">
                        <div className="px-6 py-4 border-b border-[#E5E3D8] flex items-center justify-between">
                            <h2 className="font-display text-xl font-semibold text-[#2C302E]">
                                {editingId ? "Yemeği Düzenle" : "Yeni Yemek"}
                            </h2>
                            <button onClick={close} data-testid="close-form" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F3F1EB] text-[#59605B]">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] block mb-2">
                                    Ad
                                </label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="form-name"
                                    className="w-full bg-white border border-[#E5E3D8] rounded-lg px-4 py-3 text-[#2C302E] focus:outline-none focus:border-[#D96C4A] focus:ring-1 focus:ring-[#D96C4A]"
                                    placeholder="Adana Kebap"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] block mb-2">
                                    Kategori
                                </label>
                                <input
                                    value={form.category}
                                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                                    data-testid="form-category"
                                    list="categories"
                                    className="w-full bg-white border border-[#E5E3D8] rounded-lg px-4 py-3 text-[#2C302E] focus:outline-none focus:border-[#D96C4A] focus:ring-1 focus:ring-[#D96C4A]"
                                    placeholder="Ana Yemekler"
                                />
                                <datalist id="categories">
                                    {Object.keys(grouped).map((c) => (
                                        <option key={c} value={c} />
                                    ))}
                                </datalist>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] block mb-2">
                                    Fiyat (₺)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    data-testid="form-price"
                                    className="w-full bg-white border border-[#E5E3D8] rounded-lg px-4 py-3 text-[#2C302E] focus:outline-none focus:border-[#D96C4A] focus:ring-1 focus:ring-[#D96C4A]"
                                    placeholder="195"
                                />
                            </div>
                            <label className="flex items-center gap-3 text-sm text-[#2C302E]">
                                <input
                                    type="checkbox"
                                    checked={form.available}
                                    onChange={(e) => setForm({ ...form, available: e.target.checked })}
                                    data-testid="form-available"
                                    className="w-4 h-4 accent-[#D96C4A]"
                                />
                                Stokta mevcut
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={close}
                                    className="flex-1 px-6 py-3 rounded-lg font-medium text-[#2C302E] bg-[#F3F1EB] hover:bg-[#E5E3D8]"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="form-submit"
                                    className="flex-1 px-6 py-3 rounded-lg font-medium text-white bg-[#D96C4A] hover:bg-[#C05A3B] disabled:opacity-60"
                                >
                                    {saving ? "Kaydediliyor..." : "Kaydet"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
